import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { mentionApi } from './api';
import { MobileBackButton, MobileHeader } from '@/components/mobile';
import getTeamsUrl from '@/utils/getTeamsUrl';
import { switchUser } from '@/actions';
import { setCustomerConversationAssignedType } from '@/actions/webchat';
import {
  ADD_TEAM_CONVERSATION,
  SET_ACTIVE_TEAM_CONVERSATION,
  UPDATE_TEAM_MESSAGE,
} from '@/actions/types';
export default function TeamMentionDestination() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const business = useSelector((state: any) => state.auth?._id);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Retry deliberately reruns target resolution.
  useEffect(() => {
    let active = true;
    setError('');
    const open = async () => {
      const { data: item } = await axios.get(mentionApi(`/${encodeURIComponent(id)}`, true), {
        withCredentials: true,
      });
      if (!active) return;
      if (item.unavailable) {
        setError('This message was deleted.');
        return;
      }
      if (business !== item._business) {
        await dispatch(switchUser({ _business: item._business, goToRootUrl: false }) as any);
        if (active) setError('Switching locations. If the page does not reload, please try again.');
        return;
      }
      const { data } = await axios.get(
        `${getTeamsUrl(window.location.href)}/api/team/conversation/${item._teamConversation}/messages/around/${item._teamMessage}`,
        { withCredentials: true },
      );
      if (!active) return;
      dispatch({
        type: ADD_TEAM_CONVERSATION,
        payload: { ...data.teamConversation, teamMessages: data.teamMessages },
      });
      for (const message of data.teamMessages)
        dispatch({ type: UPDATE_TEAM_MESSAGE, payload: { updatedTeamMessage: message } });
      dispatch({ type: SET_ACTIVE_TEAM_CONVERSATION, payload: item._teamConversation });
      // mentions in direct messages open in the Direct Messages view
      dispatch(
        setCustomerConversationAssignedType(item.isDirectMessage ? 'DIRECT_MESSAGES' : 'GROUPS') as any,
      );
      navigate(
        `/groupchat/${item.isDirectMessage ? 'dms' : 'groups'}/conversation/groups/${item._teamConversation}?_location=${item._business}&_teamMention=${item._id}&_target=${item._teamMessage}${item.isDirectMessage ? '&isDirectMessage=true' : ''}`,
        { replace: true },
      );
    };
    open().catch((error) => {
      if (active)
        setError(error.response?.data?.message || 'Could not open this mention. Please try again.');
    });
    return () => {
      active = false;
    };
  }, [id, business, dispatch, navigate, attempt]);
  return (
    <main className="p-8 max-md:p-0">
      <MobileHeader
        title="Team mention"
        left={<MobileBackButton fallback="/team-mentions" label="Mentions" />}
      />
      <h1 className="text-xl font-semibold max-md:sr-only">Team mention</h1>
      {error ? (
        <div role="alert" className="mt-3 space-y-3 max-md:mt-0 max-md:px-4 max-md:pt-4">
          <p>{error}</p>
          <button type="button"
            className="text-violet-700 underline dark:text-violet-300 max-md:min-h-11"
            onClick={() => setAttempt((n) => n + 1)}
          >
            Retry
          </button>
          <p className="max-md:hidden">
            <Link to="/team-mentions">Back to Team mentions</Link>
          </p>
        </div>
      ) : (
        <p className="mt-3 text-muted-foreground max-md:mt-0 max-md:px-4 max-md:pt-6 max-md:text-center" aria-live="polite">
          Opening message…
        </p>
      )}
    </main>
  );
}
